'use client';

import {useEffect, useRef, useState} from 'react';
import {ChevronDown, Loader2, X} from 'lucide-react';

const MAX_VISIBLE_OPTIONS = 20;

// V is the option's value type: free-text/cleared entries carry value: null, real
// picks carry a string. It's tied to allowCustom via the props union below.
export interface ComboboxOption<V extends string | null = string | null> {
  value: V;
  label: string;
}

interface BaseProps {
  label: string;
  placeholder?: string;
  value: string;
  // Reset the input to '' after a pick instead of showing its label (multi search mode).
  clearOnSelect?: boolean;
}

// Where the options come from (mutually exclusive). Either the component fetches and
// caches them itself (fetchOptions), or the caller owns them via `options` and may
// lazily populate them through `onFetchOptions`. Both lazy paths fire on first focus.
// Fetched options are always concrete picks (never the null free-text entry).
type OptionsSource =
  | {
      fetchOptions: () => Promise<ComboboxOption<string>[]>;
      options?: never;
      onFetchOptions?: never;
    }
  | {
      options: ComboboxOption<string>[];
      onFetchOptions?: () => void | Promise<void>;
      fetchOptions?: never;
    };

// allowCustom discriminates the prop shape: with free-text on (the default) onChange
// can receive value: null (typing/clearing/"Use …"); with it off, every onChange
// carries a concrete string value, so callers skip the null check.
type CustomMode =
  | {allowCustom?: true; onChange: (option: ComboboxOption<string | null>) => void}
  | {allowCustom: false; onChange: (option: ComboboxOption<string>) => void};

type ComboboxInputProps = BaseProps & OptionsSource & CustomMode;

function ComboboxInput({
  label,
  placeholder,
  value,
  clearOnSelect = false,
  allowCustom = true,
  fetchOptions,
  onFetchOptions,
  options: controlledOptions,
  onChange: onChangeProp,
}: ComboboxInputProps) {
  const [inputValue, setInputValue] = useState(value);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Options fetched by the component itself (fetchOptions mode); in controlled mode
  // the caller supplies them via props.options.
  const [fetchedOptions, setFetchedOptions] = useState<ComboboxOption<string>[]>([]);
  const [fetched, setFetched] = useState(false);
  const options = fetchOptions ? fetchedOptions : (controlledOptions ?? []);

  // The union narrows onChange's value type at call sites; internally we use the
  // widest signature since the body is the thing that emits the null entries.
  const onChange = onChangeProp as (option: ComboboxOption<string | null>) => void;

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // On first focus, lazily load options — either fetching them ourselves or asking
  // the caller to (onFetchOptions). A thrown load error is left to propagate.
  async function handleFocus() {
    setOpen(true);
    if (fetched) return;
    setLoading(true);
    if (fetchOptions) {
      setFetchedOptions(await fetchOptions());
    } else {
      await onFetchOptions?.();
    }
    setFetched(true);
    setLoading(false);
  }

  function handleInputChange(v: string) {
    setInputValue(v);
    if (allowCustom) onChange({value: null, label: v});
    setOpen(true);
  }

  function handleSelect(option: ComboboxOption) {
    setInputValue(clearOnSelect ? '' : option.label);
    onChange(option);
    setOpen(false);
  }

  function handleClear() {
    setInputValue('');
    if (allowCustom) onChange({value: null, label: ''});
    setOpen(false);
  }

  // Derive visible options inline — avoids extra render cycle from effect+state.
  const q = inputValue.toLowerCase();
  const matched = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  const displayed = matched.slice(0, MAX_VISIBLE_OPTIONS);
  const isUnlisted = inputValue && !options.some((o) => o.label === inputValue);

  return (
    <div className='flex flex-col gap-1.5' ref={containerRef}>
      <label className='text-sm font-semibold text-iq-black'>{label}</label>
      <div className='relative'>
        <input
          type='text'
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={handleFocus}
          placeholder={placeholder}
          className='w-full border border-iq-grey-light rounded-lg px-3 py-2.5 text-sm text-iq-black placeholder:text-iq-grey-medium focus:outline-none focus:border-iq-grey-medium transition-colors pr-8'
        />
        {/* ponytail: wrapper is click-through so the chevron area focuses the input; only the clear button opts back in */}
        <div className='absolute right-2.5 top-1/2 -translate-y-1/2 text-iq-grey-medium pointer-events-none '>
          {loading ? (
            <Loader2 className='w-4 h-4 animate-spin' />
          ) : inputValue ? (
            <button
              type='button'
              onMouseDown={handleClear}
              className='hover:text-iq-black transition-colors pointer-events-auto'
              aria-label='Clear'>
              <X className='w-4 h-4' />
            </button>
          ) : (
            <ChevronDown className='w-4 h-4' />
          )}
        </div>

        {open && (displayed.length > 0 || (allowCustom && isUnlisted)) && (
          <div className='absolute z-50 top-full mt-1 w-full bg-white border border-iq-grey-light rounded-lg shadow-lg overflow-hidden flex flex-col max-h-64'>
            <ul className='overflow-y-auto flex-1'>
              {displayed.map((option) => (
                <li key={option.value ?? option.label}>
                  <button
                    type='button'
                    onMouseDown={() => handleSelect(option)}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors hover:bg-iq-grey-lighter ${
                      option.label === value
                        ? 'text-iq-orange font-medium bg-iq-orange-light'
                        : 'text-iq-grey-dark'
                    }`}>
                    {option.label}
                  </button>
                </li>
              ))}
              {allowCustom && isUnlisted && (
                <li>
                  <button
                    type='button'
                    onMouseDown={() => handleSelect({value: null, label: inputValue})}
                    className='w-full text-left px-3 py-2 text-sm transition-colors hover:bg-iq-grey-lighter text-iq-grey-dark'>
                    Use &ldquo;{inputValue}&rdquo;
                  </button>
                </li>
              )}
            </ul>
            {matched.length > displayed.length && (
              <div className='shrink-0 border-t border-iq-grey-light px-3 py-2 text-xs text-iq-grey-medium bg-white'>
                Showing {displayed.length} of {matched.length} — type to search.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export {ComboboxInput};
